import * as Sentry from '@sentry/react';
import { createFileRoute, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { useGetBenchmarkQuery } from '@vizit/api';
import { BenchmarkImageSortOption, parseBenchmarkImageSortOption } from '@vizit/collections';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { resolveLoaderWorkspaceId, useActiveWorkspaceId } from '../../../../../hooks/use-workspace-link';
import { BenchmarkDetailsPage, PagesProvider } from '../../../../../pages';
import { beforeLoadAuthenticatedValidation, buildBenchmarkBreadcrumbs } from '../../../../../router/route-utils';
import { guardCustomBenchmarks } from '../../../../../router/route-utils';
import { BreadcrumbsPageTemplate } from '../../../../../templates';

export const Route = createFileRoute('/_authenticated/w/$workspaceSlug/library/benchmarks/$benchmarkId')({
  staticData: {
    pageName: 'Benchmark Details',
  },
  beforeLoad: async ({ context, location, params }) => {
    await beforeLoadAuthenticatedValidation(context, location);
    await guardCustomBenchmarks(params.workspaceSlug);
  },
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): { sort?: BenchmarkImageSortOption } => {
    const sort = parseBenchmarkImageSortOption(search.sort);
    // Newest is the default, so it stays out of the URL.
    return { sort: sort === BenchmarkImageSortOption.Newest ? undefined : sort };
  },
  loader: async ({ location, params, context }) => {
    const workspaceId = resolveLoaderWorkspaceId(context, params.workspaceSlug);

    let benchmarkName = 'Benchmark';

    if (workspaceId) {
      try {
        const variables = { workspace_id: workspaceId, benchmark_id: params.benchmarkId };
        const benchmark = await context.queryClient.ensureQueryData({
          queryKey: useGetBenchmarkQuery.getKey(variables),
          queryFn: useGetBenchmarkQuery.fetcher(variables),
        });
        benchmarkName = benchmark.name || benchmarkName;
      } catch (error) {
        Sentry.captureException(error, {
          level: 'warning',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Navigation,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Navigation].breadcrumbs,
          },
          extra: {
            page: 'benchmark details',
          },
        });
      }
    }

    const breadcrumbs = buildBenchmarkBreadcrumbs(
      location.state?.from,
      params.workspaceSlug,
      benchmarkName,
      location.search,
    );

    return { breadcrumbs };
  },
});

function RouteComponent() {
  const { benchmarkId } = useParams({ from: Route.id });
  const router = useRouter();
  const search = useSearch({ from: Route.id });

  const workspaceId = useActiveWorkspaceId() ?? undefined;

  const handleSortOptionChange = (option: BenchmarkImageSortOption) => {
    router.navigate({
      to: Route.fullPath,
      params: { benchmarkId },
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        sort: option === BenchmarkImageSortOption.Newest ? undefined : option,
      }),
    });
  };

  return (
    <PagesProvider template={BreadcrumbsPageTemplate}>
      <BenchmarkDetailsPage
        benchmarkId={benchmarkId}
        workspaceId={workspaceId}
        sortOption={search.sort ?? BenchmarkImageSortOption.Newest}
        onSortOptionChange={handleSortOptionChange}
      />
    </PagesProvider>
  );
}
